import React, { useEffect, useState } from 'react';
import { StyleSheet, Animated, Easing, ViewStyle, StyleProp, View, Text, TextStyle } from 'react-native';

interface MB_SpinnerSquareProps {
    /**
     * The size of the spinner.
     */
    size?: number,
    
    /**
     * The style of the spinner's container.
     */
    containerStyle?: StyleProp<ViewStyle>,
    
    /**
     * The text to display.
     */
    loadingText?: string,
    
    /**
     * The style of the text.
     */
    loadingTextStyle?: StyleProp<TextStyle>,
    
    /**
     * Whether to hide the loading text or not.
     */
    hideLoadingText?: boolean,
}

const MB_SpinnerSquare = ({ size, containerStyle, loadingText, loadingTextStyle, hideLoadingText }: MB_SpinnerSquareProps) => {
    const [spinAnimationValue] = useState(new Animated.Value(0));
    const [translateAnimationValue] = useState(new Animated.Value(0));

    useEffect(() => {
        const animateRotation = (toValue: number) => {
            Animated.timing(spinAnimationValue, {
                toValue,
                duration: 500,
                useNativeDriver: false,
            }
            ).start(() => {
                let newTranslationValue = toValue === 0 ? 1 : toValue === 1 ? 1 : 0;
                let nextRotationValue = toValue === 0 ? 1 : toValue === 1 ? 2 : 0;

                if (toValue === 2) {
                    spinAnimationValue.setValue(0);
                    newTranslationValue = 0;
                    nextRotationValue = 1;
                }
                animateTranslation(newTranslationValue, nextRotationValue);
            });
        };

        const animateTranslation = (toValue: number, nextRotationValue: number) => {
            Animated.timing(translateAnimationValue, {
                toValue,
                duration: 500,
                easing: Easing.linear,
                useNativeDriver: false,
            }
            ).start(() => {
                animateRotation(nextRotationValue);
            });
        };

        animateRotation(1);
    }, [spinAnimationValue, translateAnimationValue]);

    const spin = spinAnimationValue.interpolate({
        inputRange: [0, 1, 2],
        outputRange: ['0deg', '180deg', '360deg'],
    });

    const yVal = translateAnimationValue.interpolate({
        inputRange: [0, 1],
        outputRange: [size ? (-size) : -38 , 0],
    });

    const sizeStyle = size ? { height: size, width: size } : {};

    return (
        <View style={[styles.container, containerStyle]}>
            <Animated.View style={[styles.outerCube, sizeStyle, { transform: [{ rotate: spin }] }]}>
                <Animated.View style={[styles.innerCube, sizeStyle, { transform: [{ translateY: yVal }] }]} />
            </Animated.View>
            {!hideLoadingText &&
                <Text style={[styles.loadingText, loadingTextStyle]}>{loadingText}</Text>
            }
        </View>
    );
};

export { MB_SpinnerSquare };
export type { MB_SpinnerSquareProps };

const styles = StyleSheet.create({
    container: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    outerCube: {
        width: 38,
        height: 38,
        aspectRatio: 1,
        borderWidth: 4,
        borderColor: 'white',
        overflow: 'hidden',
    },
    innerCube: {
        backgroundColor: 'white',
        width: 38,
        height: 38,
        aspectRatio: 1,
    },
    loadingText: {
        fontSize: 16,
        fontWeight: '500',
        color: 'white',
        textAlign: 'center',
        marginTop: 16,
    },
});
